import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { categoryAddAction, uploadFileAction } from "../actions/category";
import { RoutesPaths } from "../Router/constant";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { bannerAddAction } from "../actions/banner";
import { storeDetails } from "../actions/store";

export default function AddPromotionalBanner() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /* =======================
     STATES
  ======================= */
  const [formData, setFormData] = useState({
    name: {
      en: "",
      pt: "",
    },
    description: {
      en: "",
      pt: "",
    },
    link: "",
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isimageId, setImageId] = useState();
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const { storeList } = useSelector((state) => state.store);

  /* =======================
     HANDLERS
  ======================= */

  useEffect(() => {
    dispatch(storeDetails("", "1", " "));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name.includes(".")) {
      const [field, lang] = name.split(".");

      setFormData((prev) => {
        const updatedField = {
          ...prev[field],
          [lang]: value,
        };

        const updatedData = {
          ...prev,
          [field]: updatedField,
        };

        // ✅ FIX: Only remove error if BOTH fields are filled
        if (field === "name") {
          if (
            updatedField.en.trim() !== "" &&
            updatedField.pt.trim() !== ""
          ) {
            setErrors((prevErr) => ({
              ...prevErr,
              name: "",
            }));
          }
        }

        if (field === "description") {
          if (
            updatedField.en.trim() !== "" &&
            updatedField.pt.trim() !== ""
          ) {
            setErrors((prevErr) => ({
              ...prevErr,
              description: "",
            }));
          }
        }

        return updatedData;
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      if (name === "link") {
        setErrors((prev) => ({
          ...prev,
          link: "",
        }));
      }
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // ✅ File validation
    if (!["image/png", "image/jpeg", "image/jpg"].includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        icon: "Only PNG, JPG, JPEG files are allowed",
      }));
      return;
    }
    setErrors((prev) => ({ ...prev, icon: "" }));
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    const payload = new FormData();
    payload.append("files", file);
    try {
      const response = await dispatch(uploadFileAction(payload));
      if (response?.data) {
        setImageId(response.data.data._id);
      }
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        icon: "Image upload failed",
      }));
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  /* =======================
     VALIDATION
  ======================= */
  const validate = () => {
    let newErrors = {};

    if (!formData.name.en.trim() || !formData.name.pt.trim()) {
      newErrors.name = "Both English and Portuguese names are required";
    }

    if (!imagePreview) {
      newErrors.icon = "Banner image is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleStoreChange = (e) => {
    setSelectedStoreId(e.target.value);
  };

  /* =======================
     SUBMIT
  ======================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);

    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        type: "store",
        image: isimageId,
        link: formData.link,
        storeId: selectedStoreId,
      };
      const resp = await dispatch(bannerAddAction(payload));
      if (resp.data.success) {
        navigate(RoutesPaths.PROMOTIONALBANNER);
      }
    } catch (error) {
      toast.error("error:", error);
    } finally {
      setLoading(false);
    }
  };

  const canceChanges = () => {
    navigate(RoutesPaths.PROMOTIONALBANNER);
  };

  /* =======================
     JSX
  ======================= */

  return (
    <div className="container-fluid mainConatinerSection">
      <form
        className="form d-flex flex-column flex-lg-row"
        onSubmit={handleSubmit}
      >
        {/* LEFT SIDE */}
        <div className="d-flex flex-column gap-7 gap-lg-10 w-100 w-lg-300px mb-7 me-lg-10">
          {/* IMAGE UPLOAD */}
          <div className="card card-flush py-4">
            <div className="card-header">
              <div className="card-title">
                <h2 className="required">Thumbnail</h2>
              </div>
            </div>

            <div className="card-body text-center pt-0">
              <div
                className={`image-input image-input-outline mb-3 ${imagePreview
                  ? ""
                  : "image-input-empty image-input-placeholder"
                  }`}
              >
                <div
                  className="image-input-wrapper w-150px h-150px"
                  style={{
                    backgroundImage: imagePreview
                      ? `url(${imagePreview})`
                      : "none",
                  }}
                ></div>

                <label className="btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow pencilIconSection">
                  <i className="ki-duotone ki-pencil fs-7">
                    <span className="path1 searchPath"></span>
                    <span className="path2 searchPath"></span>
                  </i>
                  <input
                    type="file"
                    accept=".png,.jpg,.jpeg"
                    hidden
                    onChange={handleImageChange}
                  />
                </label>

                {imagePreview && (
                  <span
                    className="btn btn-icon btn-circle btn-active-color-primary w-25px h-25px bg-body shadow closeIconSection"
                    onClick={handleRemoveImage}
                  >
                    <i className="ki-duotone ki-cross fs-2">
                      <span className="path1"></span>
                      <span className="path2"></span>
                    </i>
                  </span>
                )}
              </div>

              {errors.icon && (
                <div className="text-danger fs-7">{errors.icon}</div>
              )}

              <div className="text-muted fs-7">
                Only *.png, *.jpg and *.jpeg image files are accepted
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT SIDE */}
        <div className="d-flex flex-column flex-row-fluid gap-7 gap-lg-10">
          <div className="card card-flush py-4">
            <div className="card-header">
              <div className="card-title">
                <h2>General</h2>
              </div>
            </div>

            <div className="card-body pt-0">
              <div className="mb-10">
                <label className="required form-label">Banner Name (English)</label>
                <input
                  type="text"
                  name="name.en"
                  className="form-control mb-2"
                  placeholder="Banner name in English"
                  value={formData.name.en}
                  onChange={handleChange}
                />

                <label className="required form-label mt-3">Banner Name (Portuguese)</label>
                <input
                  type="text"
                  name="name.pt"
                  className="form-control mb-2"
                  placeholder="Banner name in Portuguese"
                  value={formData.name.pt}
                  onChange={handleChange}
                />

                {errors.name && (
                  <div className="text-danger fs-7">{errors.name}</div>
                )}
              </div>

              <div className="mb-10">
                <label className="form-label">Description (English)</label>
                <textarea
                  name="description.en"
                  className="form-control mb-2"
                  placeholder="Description in English"
                  value={formData.description.en}
                  onChange={handleChange}
                />

                <label className="form-label mt-3">Description (Portuguese)</label>
                <textarea
                  name="description.pt"
                  className="form-control mb-2"
                  placeholder="Description in Portuguese"
                  value={formData.description.pt}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="form-label">Banner Link</label>
                <input
                  type="text"
                  name="link"
                  className="form-control mb-2"
                  placeholder="Add link"
                  value={formData.link}
                  onChange={handleChange}
                />
              </div>

              <div className="mb-10 fv-row">
                <label className="form-label">Select store</label>

                <select
                  className="form-select mb-2"
                  value={selectedStoreId}
                  onChange={handleStoreChange}
                >
                  <option value="">Select a store</option>

                  {storeList?.data?.map((item, index) => (
                    <option key={index} value={item._id}>
                      {item.shopName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-end">
            <button className="btn btn-light me-5" onClick={canceChanges}>
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn buttonPrimary"
            >
              {!loading && <>Save Changes</>}
              {loading && (
                <div className="indicator-label">
                  <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                  Please wait...
                </div>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
